// formkit.config.js
import {generateClasses} from '@formkit/themes';
import {createInput} from '@formkit/vue';
import Selectbox from '~/components/formkit/Selectbox.vue';
import SelectCollapse from '~/components/formkit/SelectCollapse.vue';
import Slider from '~/components/formkit/Slider.vue';
import FileUpload from '~/components/formkit/FileUpload.vue';
import Datepicker from '~/components/formkit/Datepicker.vue';
import DatepickerDropdown from '~/components/formkit/DatepickerDropdown.vue';
import {checkboxExtra} from '~/components/formkit/checkbox-extra';
import PickupButton from '~/components/formkit/PickupButton.vue';
import radioExtra from '~/components/formkit/radioExtra';
import Select from '~/components/formkit/Select.vue';
import ShoppingListMergeSelect from '~/components/formkit/ShoppingListMergeSelect.vue';
import {emailCustomRule, emailCustomRuleOptional, phoneCustomRule} from '~/utils/formkitRules';

const selectProps = [
	'options',
	'placeholder',
	'disabled',
];

const selectboxProps = [
	'options',
	'multiple',
	'placeholder',
	'allOptionsLabel',
	'clearable',
	'clearLabel',
	'disabled',
];

const shoppingListMergeSelectProps = [
	'options',
	'placeholder',
	'disabled',
];

const selectClasses = {
	wrapper: 'relative',
	input: `
		flex items-center justify-between w-full py-2.5 pl-5 pr-3
		typo-body-s text-body-tertiary placeholder-body-tertiary
		border border-border-primary focus-outline focus-within:outline-border-focus
		transition-colors
		truncate
	`,
	label: 'form-label',
};

const radioClasses = {
	wrapper: 'cursor-pointer data-[checked=true]:font-semibold',
	inner: 'formkit-disabled:bg-transparent formkit-disabled:opacity-50',
	input: 'sr-only',
	decorator: 'rounded-full',
};

const checkboxClasses = {
	wrapper: 'cursor-pointer',
	inner: 'formkit-disabled:bg-transparent formkit-disabled:opacity-50',
	decorator: 'rounded-sm',
};

const fileClasses = {
	label: 'form-label text-functional-error-primary',
	outer: 'group max-w-[20em] min-w-0 grow mb-4 data-[disabled]:select-none data-[disabled]:opacity-50',
	wrapper: 'flex flex-col items-start justify-start mb-1.5 last:mb-0',
	inner: 'flex items-center w-full py-2 px-3 rounded-sm border border-border-primary bg-white focus-within:ring-1 focus-within:!ring-orange-400 focus-within:!border-orange-400 group-data-[invalid]:border-functional-error-primary group-data-[invalid]:ring-1 group-data-[invalid]:ring-functional-error-primary group-data-[disabled]:bg-background-disabled group-data-[disabled]:!cursor-not-allowed shadow-sm group-[]/repeater:shadow-none group-[]/multistep:shadow-none relative cursor-pointer group-data-[has-multiple]:rounded-sm',
	input: 'appearance-none selection:text-body-secondary group-data-[has-overlay]:selection:!text-transparent text-body-secondary min-w-0 min-h-[1.5em] grow outline-hidden bg-transparent selection:bg-blue-500 placeholder:text-body-tertiary group-data-[disabled]:!cursor-not-allowed border-none p-0 focus:ring-0 cursor-pointer text-transparent absolute inset-0 opacity-0 z-10 file:pointer-events-none file:w-0 file:h-0 file:overflow-hidden',
	noFiles: 'flex w-full items-center text-body-tertiary px-1',
	fileList: 'group/list peer w-full min-w-0 data-[has-multiple]:mb-[1.25em]',
	fileItem: 'flex min-w-0 items-center text-body-secondary mb-1.5 last:mb-0',
	fileRemove: 'right-2 ring-orange-400 rounded-sm z-20 flex appearance-none items-center text-[0px] outline-hidden hover:text-functional-error-primary! focus-visible:ring-2 group-data-[disabled]:pointer-events-none group-data-[disabled]:!text-body-disabled peer-data-[has-multiple]:absolute peer-data-[has-multiple]:bottom-[max(0.5em,8px)] peer-data-[has-multiple]:left-3 peer-data-[has-multiple]:text-blue-1000 peer-data-[has-multiple]:typo-body-caption peer-data-[has-multiple]:whitespace-nowrap group-data-[prefix-icon]:peer-data-[has-multiple]:left-2',
};

export default {
	locale: 'cs',
	config: {
		classes: generateClasses({
			global: {
				help: 'mt-1 typo-body-caption text-body-secondary',
				inner: 'formkit-disabled:bg-background-disabled formkit-disabled:cursor-not-allowed formkit-disabled:pointer-events-none',
				input: 'appearance-none rounded-lg border bg-background-secondary active-visible:bg-white focus-visible:bg-white transition-all duration-300',
				message: 'mb-1 typo-body-caption',
				messages: 'list-none p-0 mt-1 mb-0 text-functional-error-primary',
			},
			'family:button': {input: '$reset flex items-center font-semibold rounded-full'},
			'family:text': {
				inner: 'flex items-center rounded-lg',
				input: 'w-full px-4 py-2 typo-body-s border-border-primary active:border-border-select focus:border-border-select text-border-select placeholder-body-tertiary outline-hidden focus:outline-hidden formkit-invalid:border-functional-error-primary',
				label: 'form-label',
			},
			'family:box': {
				decorator: `
					block
					relative
					size-6
					mr-3
					transition-colors
					text-transparent
					bg-background-secondary
					ring-1
					ring-border-primary
					outline-hidden
					focus:outline-hidden
					peer-focus:outline-border-focus
					hover:ring-border-select
					peer-active:ring-border-select
					peer-active:bg-background-tertiary
					peer-checked:text-icon-active
					peer-disabled:ring-border-disabled
					peer-disabled:bg-background-disabled
					formkit-invalid:ring-functional-error-primary
				`,
				help: 'mb-2 mt-1.5',
				input: 'absolute w-0 h-0 overflow-hidden opacity-0 pointer-events-none peer',
				label: '$reset typo-body-s text-body-primary select-none',
				wrapper: 'flex items-center mb-3',
			},
			select: selectClasses,
			checkbox: checkboxClasses,
			checkboxExtra: checkboxClasses,
			radio: radioClasses,
			radioExtra: radioClasses,
			textarea: {
				inner: 'flex max-w-md rounded-lg',
				input: 'block w-full h-32 px-3 py-3 typo-body-s text-body-primary placeholder-body-tertiary border-border-primary active:border-border-select bg-background-secondary active:bg-white focus-visible:bg-white outline-hidden focus:outline-hidden transition-all duration-300',
				label: 'form-label',
			},
			file: fileClasses,
			fileUpload: {
				inner: 'rounded-lg bg-background-secondary active:bg-white focus-visible:bg-white border-2 border-dashed border-border-primary',
				input: 'hidden',
				label: 'form-label',
				selectFile: 'flex text-body-secondary items-center justify-between typo-body-s py-2.5 px-3 cursor-pointer',
				dropZone: 'p-3 bg-orange-100 rounded-sm flex items-center justify-center rem:min-h-[116px] border-2 border-dashed border-orange-400 text-body-secondary',
				messages: 'hidden',
			},
			selectbox: selectClasses,
			shoppingListMergeSelect: {
				...selectClasses,
				label: 'block typo-body-s text-body-secondary mb-2',
				input: `
					flex items-center justify-between w-full py-2 px-4
					typo-body-s text-body-tertiary placeholder-body-tertiary
					border border-border-primary focus-outline focus-within:outline-border-focus
					transition-colors
				`,
			},
			PickupButton: {
				inner: 'rounded-lg',
				input: 'rounded-lg w-full flex items-center',
				inputLabel: 'w-full text-left px-4 py-2 typo-body-s',
			},
			search: {
				inner: '$reset flex items-center max-w-md px-5 rounded-full bg-background-primary border border-border-secondary shadow-green-sm',
				input: '$reset w-full pl-2 py-2 border-none bg-transparent typo-body-s text-body-primary placeholder-body-tertiary appearance-none shadow-none outline-hidden focus:outline-hidden',
			},
		}),
	},
	inputs: {
		select: createInput(Select, {props: selectProps}),
		shoppingListMergeSelect: createInput(ShoppingListMergeSelect, {props: shoppingListMergeSelectProps}),
		selectbox: createInput(Selectbox, {props: selectboxProps}),
		selectCollapse: createInput(SelectCollapse, {props: selectboxProps}),
		slider: createInput(Slider, {props: ['min', 'max', 'step']}),
		fileUpload: createInput(FileUpload, {}),
		datepicker: createInput(Datepicker, {}),
		datepickerDropdown: createInput(DatepickerDropdown, {}),
		PickupButton: createInput(PickupButton, {}),
		checkboxExtra,
		radioExtra,
	},
	rules: {
		emailCustomRule,
		emailCustomRuleOptional,
		phoneCustomRule,
	},
	messages: {
		cs: {
			validation: {
				emailCustomRule(): string {
					return 'Vámi zadaná e-mailová adresa není platná.';
				},
				emailCustomRuleOptional(): string {
					return 'Vámi zadaná e-mailová adresa není platná.';
				},
				phoneCustomRule(): string {
					return `Vámi zadané telefonní číslo není platné.
					Zadejte telefonní číslo ve formátu 123456789.`;
				},
				required: 'Toto pole je povinné.',
				length: 'Zadejte správný počet znaků.',
			},
		},
	},
};
